import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { readStoredAppVersion, writeStoredAppVersion } from "@/constants/appVersionStorage";
import { clearAppCache } from "services/cacheManager";
import { appVersionKeys } from "queries/appVersion.keys";
import { consumeDocumentLevelManifestVersionSync } from "utils/appVersionDocumentSync";
import { fetchAppVersionManifest, type AppVersionManifest } from "utils/appVersionManifest";

export interface UseAppVersionResult {
  hasUpdate: boolean;
  applying: boolean;
  applyUpdate: () => Promise<void>;
  currentVersion?: string;
  latestVersion?: string;
  latestManifest?: AppVersionManifest;
}

const DEFAULT_REFETCH_MS = 5 * 60 * 1000;

const isTestEnvironment =
  (typeof window !== "undefined" &&
    window.location.hostname === "localhost" &&
    window.location.port === "3001") ||
  process.env.NODE_ENV === "test";

export const useAppVersion = (
  refetchIntervalMs: number = DEFAULT_REFETCH_MS
): UseAppVersionResult => {
  const [storedVersion, setStoredVersion] = useState<string | null>(() =>
    typeof window !== "undefined" ? readStoredAppVersion() : null
  );
  const [applying, setApplying] = useState(false);

  const { data: manifest } = useQuery({
    queryKey: appVersionKeys.manifest(),
    queryFn: fetchAppVersionManifest,
    staleTime: 90 * 1000,
    refetchInterval: isTestEnvironment ? false : refetchIntervalMs,
    refetchOnWindowFocus: true,
    retry: false,
  });

  useEffect(() => {
    if (!manifest?.version) return;
    if (!consumeDocumentLevelManifestVersionSync()) return;
    writeStoredAppVersion(manifest.version);
    setStoredVersion(manifest.version);
  }, [manifest?.version]);

  const hasUpdate = Boolean(
    storedVersion && manifest?.version && storedVersion !== manifest.version
  );

  const applyUpdate = useCallback(async () => {
    if (applying) return;
    try {
      setApplying(true);
      const latest = await fetchAppVersionManifest();
      writeStoredAppVersion(latest.version);
      setStoredVersion(latest.version);
      await clearAppCache();
      window.setTimeout(() => {
        window.location.reload();
      }, 200);
    } catch {
      setApplying(false);
    }
  }, [applying]);

  return {
    hasUpdate,
    applying,
    applyUpdate,
    currentVersion: storedVersion ?? undefined,
    latestVersion: manifest?.version,
    latestManifest: manifest,
  };
};
